import React from 'react';

interface CashFlowIndicatorProps {
  type: 'deposit' | 'withdrawal' | null;
  amount: number;
  confidence?: number;
  verified?: boolean;
  tradingChange?: number;
  tradingPercent?: number;
}

const CashFlowIndicator: React.FC<CashFlowIndicatorProps> = ({
  type,
  amount,
  confidence = 0,
  verified = false,
  tradingChange = 0,
  tradingPercent = 0,
}) => {
  const formatCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(absValue);
    return `${value >= 0 ? '+' : '-'}$${formatted}`;
  };

  const formatPercent = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Create confidence meter visualization
  const renderConfidenceMeter = () => {
    if (!confidence) return null;

    const bars = 5;
    const filledBars = Math.round((confidence / 100) * bars);

    return (
      <span className="bb-confidence-meter">
        {[...Array(bars)].map((_, i) => (
          <span
            key={i}
            className={`bb-confidence-bar ${i < filledBars ? 'filled' : ''}`}
          />
        ))}
        <span style={{ marginLeft: '4px', fontSize: '10px', color: 'var(--bb-text-secondary)' }}>
          {confidence}%
        </span>
      </span>
    );
  };

  if (!type) {
    // Regular trading day - show normal change
    return (
      <div className="flex items-center justify-end">
        <span className={tradingChange >= 0 ? 'bb-value-positive' : 'bb-value-negative'}>
          {formatCurrency(tradingChange)}
        </span>
        <span
          className={`ml-2 text-xs ${tradingChange >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}
        >
          ({formatPercent(tradingPercent)})
        </span>
        <span className="bb-status-indicator bb-status-t ml-2">[T]</span>
      </div>
    );
  }

  // Cash flow day - show two-line format
  return (
    <div className="space-y-1">
      {/* Line 1: Trading Performance */}
      <div className="flex items-center justify-end">
        <span className="text-xs" style={{ color: 'var(--bb-text-secondary)' }}>
          TRADING:
        </span>
        <span
          className={`ml-2 ${tradingChange >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}
        >
          {formatCurrency(tradingChange)}
        </span>
        <span
          className={`ml-2 text-xs ${tradingChange >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`}
        >
          ({formatPercent(tradingPercent)})
        </span>
      </div>

      {/* Line 2: Cash Flow Event */}
      <div className="flex items-center justify-end">
        <span
          className={type === 'deposit' ? 'bb-cash-flow-deposit' : 'bb-cash-flow-withdrawal'}
          style={{ fontSize: '11px' }}
        >
          {type === 'deposit' ? 'DEPOSIT' : 'WITHDRAWAL'}:
        </span>
        <span
          className={`ml-2 ${type === 'deposit' ? 'bb-cash-flow-deposit' : 'bb-cash-flow-withdrawal'}`}
          style={{ fontWeight: 'bold' }}
        >
          {formatCurrency(amount)}
        </span>
        {renderConfidenceMeter()}
        {verified && (
          <span
            className="ml-2 text-xs"
            style={{ color: 'var(--bb-cyan)' }}
            title="Verified"
          >
            ✓
          </span>
        )}
        <span
          className={`bb-status-indicator ml-2 ${
            type === 'deposit' ? 'bb-status-d' : 'bb-status-w'
          }`}
        >
          [{type === 'deposit' ? 'D' : 'W'}]
        </span>
      </div>
    </div>
  );
};

export default CashFlowIndicator;